import { useState, useRef } from "react";
import { RevisionUploadFileService } from "shared/services/revision";
import { handleErrors } from "shared/utils/helper";
import { toastMessage } from "shared/components/common/toast";

export const useRevisionUpload = () => {
  const [uploadedFileIds, setUploadedFileIds] = useState<number[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number;
  }>({});
  const [currentFiles, setCurrentFiles] = useState<File[]>([]);
  const [fileIdMap, setFileIdMap] = useState<Map<string, number>>(new Map());
  const processingFilesRef = useRef<string>("");
  const hasShownToastRef = useRef<boolean>(false);

  const getFileKey = (file: File) =>
    `${file.name}-${file.size}-${file.lastModified}`;

  const uploadFileWithProgress = async (
    file: File
  ): Promise<number | undefined> => {
    try {
      setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));

      const { formData, uploadUrl, token } = RevisionUploadFileService(file);

      const uploadPromise = new Promise<any>((resolve, reject) => {
        fetch(uploadUrl, {
          method: "POST",
          body: formData,
          headers: {
            ...(token && { authorization: `Bearer ${token}` }),
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let finalData: any = null;

            const readStream = () => {
              reader?.read().then(({ done, value }) => {
                if (done) {
                  if (finalData) {
                    resolve({
                      status: 200,
                      data: finalData,
                    });
                  } else {
                    reject(new Error("No data received from upload"));
                  }
                  return;
                }

                buffer += decoder.decode(value, { stream: true });

                const lines = buffer.split("\n");
                buffer = lines.pop() || "";

                for (const line of lines) {
                  if (line.startsWith("data: ")) {
                    try {
                      const jsonStr = line.substring(6);
                      const data = JSON.parse(jsonStr);

                      if (data.progress !== undefined) {
                        // Cap streamed progress at 99 — 100 is set below only
                        // once the upload record is confirmed, so the UI's
                        // "complete" state matches actual completion.
                        setUploadProgress((prev) => ({
                          ...prev,
                          [file.name]: Math.min(
                            Math.max(data.progress, 0),
                            99,
                          ),
                        }));
                      }

                      if (data.data && data.data.revision_designer_upload_id) {
                        finalData = data.data;
                      }
                    } catch (e) {
                      console.error("Error parsing SSE data:", e);
                    }
                  }
                }
                readStream();
              });
            };

            readStream();
          })
          .catch((error) => {
            reject(error);
          });
      });

      const response: any = await uploadPromise;

      if (response.status === 200 || response.status === 201) {
        const fileId =
          response.data?.revision_designer_upload_id ||
          response.data?.file_id ||
          response.data?.id;

        if (!fileId) {
          console.error("Upload response data:", response.data);
          throw new Error("No file ID received from server");
        }

        setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
        return fileId;
      }

      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[file.name];
        return newProgress;
      });

      throw new Error(`Upload failed with status ${response.status}`);
    } catch (error: any) {
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[file.name];
        return newProgress;
      });

      handleErrors(error);
    }
  };

  const handleFilesSelected = async (
    files: File[],
    isAddingMore: boolean = false
  ) => {
    if (!files || files.length === 0) return;

    const existingFileKeys = new Set(currentFiles.map(getFileKey));
    const newFiles = files.filter(
      (file) => !existingFileKeys.has(getFileKey(file))
    );

    if (newFiles.length === 0 && isAddingMore) {
      return;
    }

    try {
      setUploadingFiles(true);
      hasShownToastRef.current = false; 

      if (!isAddingMore) {
        setUploadedFileIds([]);
        setCurrentFiles([]);
      }

      const uploadedIds: number[] = isAddingMore ? [...uploadedFileIds] : [];
      const newFileIdMap = new Map(isAddingMore ? fileIdMap : []);
      let failedCount = 0;
      const filesToUpload = isAddingMore ? newFiles : files;

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];

        try {
          const fileId = await uploadFileWithProgress(file);

          if (fileId) {
            uploadedIds.push(fileId);
            newFileIdMap.set(getFileKey(file), fileId);
          } else {
            failedCount++;
          }
        } catch (error: any) {
          handleErrors(error);
          failedCount++;
        }
      }

      if (!hasShownToastRef.current) {
        if (uploadedIds.length > 0) {
          setUploadedFileIds(uploadedIds);
          setFileIdMap(newFileIdMap);
          setCurrentFiles(files);

          if (failedCount > 0) {
            const successCount = filesToUpload.length - failedCount;
            const successText = successCount === 1 ? "file" : "files";
            const failText = failedCount === 1 ? "file" : "files";
            toastMessage(
              "warning",
              `${successCount} ${successText} uploaded successfully, ${failedCount} ${failText} failed`
            );
            hasShownToastRef.current = true;
          } else {
            const fileText = filesToUpload.length === 1 ? "file" : "files";
            toastMessage(
              "success",
              `Successfully uploaded ${filesToUpload.length} ${fileText}`
            );
            hasShownToastRef.current = true;
          }
        } else if (failedCount > 0) {
          const fileText = failedCount === 1 ? "file" : "files";
          toastMessage("error", `All ${failedCount} ${fileText} failed to upload`);
          hasShownToastRef.current = true;
          if (!isAddingMore) {
            setCurrentFiles([]);
          }
        }
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setUploadingFiles(false);
      setTimeout(() => setUploadProgress({}), 3000);
    }
  };

  const resetUploadState = () => {
    setUploadedFileIds([]);
    setCurrentFiles([]);
    setFileIdMap(new Map());
    setUploadProgress({});
    processingFilesRef.current = "";
  };

  return {
    uploadedFileIds,
    uploadingFiles,
    uploadProgress,
    currentFiles,
    fileIdMap,
    processingFilesRef,
    getFileKey,
    handleFilesSelected,
    setUploadedFileIds,
    setCurrentFiles,
    setFileIdMap,
    resetUploadState,
  };
};

